// src/pages/CoachLandingPage.jsx

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiTarget, FiTrendingUp, FiUsers, FiZap, FiMail, FiCheckCircle } from 'react-icons/fi';
import { db, getEnvironmentTag } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { setLanguage } from '../i18n';
import PageMeta from '../components/PageMeta';
import { trackCTAClick, trackWaitlistSubmit, trackWaitlistError } from '../utils/analytics';
import './CoachLandingPage.css';

const DEMO_CALENDAR_URL = 'https://calendar.app.google/BPMY9TM7BygWDzSv6';

const Logo = ({ className = "" }) => {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <svg width="36" height="32" viewBox="0 0 36 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="12" y="0" width="12" height="12" rx="3" fill="#06b6d4" />
        <rect x="0" y="16" width="12" height="12" rx="3" fill="#06b6d4" fillOpacity="0.4" />
        <rect x="24" y="16" width="12" height="12" rx="3" fill="#06b6d4" fillOpacity="0.7" />
      </svg>
      <span className="font-display font-extrabold text-3xl tracking-tight text-white">Podium</span>
    </div>
  );
};

const WaitlistForm = ({ t, i18n, source = 'coaches_page' }) => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email || !email.includes('@')) {
      setErrorMessage(t('landing.waitlist.errorInvalid', 'E-mail inválido'));
      trackWaitlistError({ role: 'coach', source, reason: 'invalid_email' });
      return;
    }

    setIsLoading(true);

    try {
      const normalizedEmail = email.toLowerCase().trim();
      const normalizedLang = (i18n.language || 'pt').toLowerCase().startsWith('en') ? 'en' : 'pt';
      const environment = getEnvironmentTag();

      await addDoc(collection(db, 'waitlist'), {
        email: normalizedEmail,
        createdAt: serverTimestamp(),
        source: source,
        status: 'pending',
        language: normalizedLang,
        role: 'coach',
        environment
      });

      trackWaitlistSubmit({ role: 'coach', source, email: normalizedEmail, language: normalizedLang });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error adding to waitlist:', error);
      setErrorMessage(t('landing.waitlist.errorGeneric', 'Erro ao salvar. Tente novamente.'));
      trackWaitlistError({ role: 'coach', source, reason: 'firestore_write_failed' });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="waitlist-success">
        <FiCheckCircle className="success-icon" />
        <h3>{t('landing.waitlist.successMessage', 'Sua vaga foi reservada! Fique de olho no e-mail.')}</h3>
        <p>{t('landing.waitlist.successMessageBody', 'Em breve você receberá um e-mail com os próximos passos.')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="waitlist-form">
      <div className="waitlist-input-group-container">
        <div className="waitlist-input-group">
          <FiMail className="input-icon" />
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errorMessage) setErrorMessage('');
            }}
            placeholder={t('landing.waitlist.inputPlaceholder_v2', 'Seu melhor e-mail')}
            className="waitlist-input"
            disabled={isLoading || isSubmitted}
          />
          <button
            type="submit"
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold rounded-lg shadow-md hover:from-cyan-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 transition-all duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading || isSubmitted}
          >
            {isLoading ? t('landing.waitlist.buttonProcessing', 'Garantindo vaga...') : t('coachLanding.waitlist.cta', 'Quero Saber Mais')}
          </button>
        </div>

        {errorMessage && (
          <div className="waitlist-error-inline">
            ⚠️ {errorMessage}
          </div>
        )}
      </div>
    </form>
  );
};

const CoachLandingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLanguageSwitch = (lang) => {
    setLanguage(lang);
    document.documentElement.lang = lang;

    const currentPath = location.pathname;
    const newPath = lang === 'en' ? '/coaches' : '/treinadores';

    if (newPath !== currentPath) {
      navigate(newPath);
    }
  };

  const isEn = i18n.language?.startsWith('en');

  return (
    <div className="coach-landing-page">
      <PageMeta
        role="coach"
        lang={isEn ? 'en' : 'pt'}
        path={isEn ? '/coaches' : '/treinadores'}
        image="/hero-coach.jpg"
        title={isEn
          ? 'Podium for Coaches | The AI writes the draft. You sign.'
          : 'Podium para Treinadores | A IA escreve o rascunho. Você assina.'}
        description={isEn
          ? 'Mariana AI drafts the weekly plan from real Strava data. You review, edit and approve — nothing reaches your athlete without you.'
          : 'A IA Mariana rascunha o plano semanal a partir dos dados reais do Strava. Você revisa, edita e aprova — nada chega ao seu atleta sem passar por você.'}
      />

      {/* Absolute Header Elements (Transparent) */}
      <div className="coach-header-container" style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '2rem', zIndex: 1000, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="flex-shrink-0 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <Logo />
        </div>

        <div className="flex items-center gap-4">
          <a
            href="https://app.podiumrun.com/login"
            onClick={() => trackCTAClick({ role: 'coach', ctaId: 'login', location: 'header' })}
            style={{
              color: 'rgba(255, 255, 255, 0.9)',
              textDecoration: 'none',
              fontSize: '0.95rem',
              fontWeight: '500',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              display: 'inline-block'
            }}
          >
            {i18n.language.startsWith('pt') ? 'Entrar' : 'Log in'}
          </a>

          <Link
            to={i18n.language.startsWith('en') ? '/runners' : '/atletas'}
            style={{
              color: 'rgba(255, 255, 255, 0.9)',
              textDecoration: 'none',
              fontSize: '0.95rem',
              fontWeight: '500',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              transition: 'all 0.2s ease',
              display: 'inline-block',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(139, 92, 246, 0.15)';
              e.target.style.borderColor = 'rgba(139, 92, 246, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            {i18n.language.startsWith('pt') ? '→ Para Atletas' : '→ For Athletes'}
          </Link>

          <div className="language-switcher-overlay" style={{ position: 'relative', top: 'auto', right: 'auto', display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => handleLanguageSwitch('pt')}
              className={`lang-btn ${i18n.language.startsWith('pt') ? 'active' : ''}`}
              title="Português"
            >
              <svg className="flag-icon" viewBox="0 0 24 16" xmlns="http://www.w3.org/2000/svg" style={{ width: '32px', height: '22px', minWidth: '32px' }}>
                <rect width="24" height="16" fill="#009b3a" />
                <path fill="#fedf00" d="M12 2L21.5 8 12 14 2.5 8z" />
                <circle fill="#002776" cx="12" cy="8" r="3.5" />
                <path fill="#fff" d="M9 7.5a4 2 0 1 0 6 0" />
              </svg>
            </button>
            <button
              onClick={() => handleLanguageSwitch('en')}
              className={`lang-btn ${i18n.language.startsWith('en') ? 'active' : ''}`}
              title="English"
            >
              <svg className="flag-icon" viewBox="0 0 24 16" xmlns="http://www.w3.org/2000/svg" style={{ width: '32px', height: '22px', minWidth: '32px' }}>
                <rect width="24" height="16" fill="#fff" />
                <rect width="6" height="16" fill="#ff0000" />
                <rect x="18" width="6" height="16" fill="#ff0000" />
                <path fill="#ff0000" d="M12 4l.5 2h1.5l-1.2 1 .5 2-1.3-1-1.3 1 .5-2-1.2-1h1.5z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="coach-hero-section">
        <div className="coach-hero-overlay"></div>
        <div className="coach-hero-background"></div>

        <div className="coach-hero-content">
          <h1 className="coach-hero-title">
            {t('coachLanding.hero.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">{t('coachLanding.hero.titleAccent')}</span>{t('coachLanding.hero.titleEnd')}
          </h1>
          <p className="coach-hero-subtitle">
            {t('coachLanding.hero.subtitle')}
          </p>
          <a
            href={DEMO_CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTAClick({ role: 'coach', ctaId: 'book_demo', location: 'hero' })}
            className="inline-flex items-center gap-2 px-10 py-5 rounded-xl font-bold text-lg text-white bg-gradient-to-r from-[#06b6d4] to-[#8b5cf6] hover:from-[#06b6d4] hover:to-[#7c3aed] transition-all shadow-[0_0_30px_rgba(139,92,246,0.3)] hover:shadow-[0_0_50px_rgba(139,92,246,0.5)] transform hover:-translate-y-1 no-underline"
            style={{ textDecoration: 'none' }}
          >
            <span>{t('coachLanding.hero.cta')}</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
          </a>
        </div>
      </section>

      {/* Manifesto Section */}
      <section className="coach-manifesto-section">
        <div className="coach-section-container">
          <div className="coach-manifesto-content">
            <h2 className="coach-section-title">
              {t('coachLanding.manifesto.title')} <span className="coach-primary">{t('coachLanding.manifesto.titleAccent')}</span>?
            </h2>
            <p className="coach-manifesto-text">
              {t('coachLanding.manifesto.text')}
              <span className="coach-accent-bold"> {t('coachLanding.manifesto.highlight')}</span>
            </p>
          </div>
        </div>
      </section>

      {/* Convite Section */}
      <section className="coach-convite-section">
        <div className="coach-section-container">
          <div className="coach-convite-content">
            <div className="coach-convite-header">
              <h2 className="coach-section-title">
                {t('coachLanding.convite.title')} <span className="coach-primary">{t('coachLanding.convite.titleAccent')}</span>?
              </h2>
              <p className="coach-convite-subtitle">
                {t('coachLanding.convite.subtitle')}
              </p>
            </div>

            <div className="coach-features-grid">
              <div className="coach-feature-item">
                <div className="coach-feature-icon">
                  <FiTarget />
                </div>
                <p className="coach-feature-text">
                  {t('coachLanding.convite.feature1')}
                </p>
              </div>

              <div className="coach-feature-item">
                <div className="coach-feature-icon">
                  <FiTrendingUp />
                </div>
                <p className="coach-feature-text">
                  {t('coachLanding.convite.feature2')}
                </p>
              </div>

              <div className="coach-feature-item">
                <div className="coach-feature-icon">
                  <FiUsers />
                </div>
                <p className="coach-feature-text">
                  {t('coachLanding.convite.feature3')}
                </p>
              </div>

              <div className="coach-feature-item">
                <div className="coach-feature-icon">
                  <FiZap />
                </div>
                <p className="coach-feature-text">
                  {t('coachLanding.convite.feature4')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefícios Section */}
      <section className="coach-benefits-section">
        <div className="coach-section-container">
          <div className="coach-benefits-content">
            <div className="coach-benefits-header">
              <h2 className="coach-section-title">
                {t('coachLanding.benefits.title')} <span className="coach-accent">{t('coachLanding.benefits.titleAccent')}</span>
              </h2>
            </div>

            <div className="coach-benefits-grid">
              <div className="coach-benefit-card">
                <div className="coach-benefit-icon coach-benefit-icon-accent">
                  <FiZap />
                </div>
                <h3 className="coach-benefit-title">{t('coachLanding.benefits.card1.title')}</h3>
                <p className="coach-benefit-text">
                  {t('coachLanding.benefits.card1.text')}
                </p>
              </div>

              <div className="coach-benefit-card">
                <div className="coach-benefit-icon coach-benefit-icon-primary">
                  <FiTarget />
                </div>
                <h3 className="coach-benefit-title">{t('coachLanding.benefits.card2.title')}</h3>
                <p className="coach-benefit-text">
                  {t('coachLanding.benefits.card2.text')}
                </p>
              </div>

              <div className="coach-benefit-card">
                <div className="coach-benefit-icon coach-benefit-icon-accent">
                  <FiTrendingUp />
                </div>
                <h3 className="coach-benefit-title">{t('coachLanding.benefits.card3.title')}</h3>
                <p className="coach-benefit-text">
                  {t('coachLanding.benefits.card3.text')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final Section */}
      <section className="coach-cta-final-section">
        <div className="coach-cta-final-overlay"></div>
        <div className="coach-cta-final-content">
          <h2 className="coach-cta-final-title">
            {t('coachLanding.ctaFinal.title')} <span className="coach-accent">{t('coachLanding.ctaFinal.titleAccent')}</span> {t('coachLanding.ctaFinal.titleEnd')}
          </h2>
          <p className="coach-cta-final-subtitle">
            {t('coachLanding.ctaFinal.subtitle')}
          </p>

          {/* Waitlist Form */}
          <div className="coach-waitlist-container">
            <WaitlistForm t={t} i18n={i18n} source="coaches_page" />
          </div>

          <div className="coach-cta-divider">
            <span>{t('coachLanding.ctaFinal.or', 'ou')}</span>
          </div>

          <a
            href={DEMO_CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTAClick({ role: 'coach', ctaId: 'book_demo', location: 'cta_final' })}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '1rem 2rem',
              borderRadius: '0.75rem',
              fontWeight: '700',
              color: '#ffffff',
              background: 'linear-gradient(to right, #06b6d4, #8b5cf6)',
              textDecoration: 'none',
              transition: 'all 0.2s',
              boxShadow: '0 0 20px rgba(139, 92, 246, 0.4)',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 0 30px rgba(139, 92, 246, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 0 20px rgba(139, 92, 246, 0.4)';
            }}
          >
            {t('coachLanding.ctaFinal.cta')}
          </a>
        </div>
      </section>

      {/* Styles for simple scroll transitions (fade-in-up) */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .coach-hero-content, .coach-manifesto-content, .coach-convite-content, .coach-benefits-content, .coach-cta-final-content {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        /* Stagger animations based on section index roughly */
        .coach-manifesto-content { animation-delay: 0.2s; }
        .coach-convite-content { animation-delay: 0.4s; }
        .coach-benefits-content { animation-delay: 0.6s; }
        
        .coach-benefit-card {
           transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .coach-benefit-card:hover {
           transform: translateY(-5px);
           box-shadow: 0 10px 30px -10px rgba(139, 92, 246, 0.3);
        }
      `}</style>
    </div>
  );
};

export default CoachLandingPage;
